/* eslint-disable default-case */
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import React, { useContext, useEffect } from 'react'

import colorContext from '../../../../Context/colorContext'
import { useDevice } from '../../../../hooks/use.device'
import { Props } from './types/index.d'
import { useVideos } from './use.videos'

export default function Videos({ videoList }: Props): JSX.Element {
  const {
    selectedVideo,
    setSelectedVideo,
    history,
    matches,
    filteredVideoList,
    videoController,
  } = useVideos(videoList)
  const { colors } = useContext(colorContext)
  const { device } = useDevice()
  const useStyles = makeStyles((theme: Theme) => ({
    main: {
      gap: '2.5rem',
      width: '100%',
      display: 'flex',
      padding: `2.5rem .5rem`,
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundColor: '#F7F7F7',
    },
    titleContainer: {
      gap: '1rem',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    title: {
      fontWeight: 700,
      fontFamily: 'Roboto',
      lineHeight: '36px',
      color: colors.accentColor,
      textTransform: 'uppercase',
      textUnderlinePosition: 'from-font',
      fontSize: theme.typography.pxToRem(30),
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(24),
      },
    },
    videosContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      maxWidth: '1280px',
      minHeight: '544px',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    videoMain: {
      width: '100%',
      height: '100%',
      display: 'flex',
      maxWidth: `${device === 'desktop' ? '880px' : '100%'}`,
      alignItems: 'center',
      justifyContent: 'center',
    },
    moreVideos: {
      width: '100%',
      height: '100%',
      maxWidth: `${device === 'desktop' ? '400px' : '100%'}`,
      borderRadius: `${matches ? '0' : '8px'}`,
      backgroundColor: theme.palette.common.white,
    },
    videPreview: {
      gap: '1rem',
      width: '100%',
      height: '100%',
      padding: '1rem',
      display: 'flex',
      cursor: 'pointer',
      minHeight: '136px',
      maxHeight: '136px',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    videPreviewSelected: {
      gap: '1rem',
      width: '100%',
      height: '100%',
      padding: '1rem',
      display: 'flex',
      minHeight: '136px',
      maxHeight: '136px',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      color: theme.palette.common.white,
      backgroundColor: colors.accentColor,
    },
    imageVideoPreview: {
      width: '100%',
      height: '100%',
      maxWidth: '104px',
      maxHeight: '104px',
      objectFit: 'cover',
      borderRadius: '8px',
      objectPosition: 'center',
    },
    titleVideoPreview: {
      fontWeight: 500,
      fontSize: theme.typography.pxToRem(18),
      textAlign: 'left',
      lineHeight: '24px',
      fontFamily: 'Roboto',
      textUnderlinePosition: 'from-font',
    },
    icon: {
      width: '100%',
      height: '100%',
      maxWidth: '36px',
      color: theme.palette.common.white,
    },
    iconTitle: {
      width: '36px',
      color: colors.accentColor,
    },
    iconNavite: {
      color: theme.palette.common.white,
      cursor: 'pointer',
      width: 32,
      height: 32,
      background: colors.accentColorBackground,
      padding: 0,
      '&:hover': {
        backgroundColor: colors.cardAccentColor,
        color: theme.palette.common.white,
      },
    },
    button: {
      width: '100%',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      maxWidth: 200,
      height: 46,
      transition: 'opacity 0.3s ease',
      borderRadius: 0,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        opacity: '0.8',
      },
    },
    textButton: {
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(14),
      textTransform: 'uppercase',
    },
  }))
  const classes = useStyles()

  const renderVideoMain = () => (
    <iframe
      frameBorder="0"
      allowFullScreen
      title="YouTube video player"
      src={`https://www.youtube.com/embed/${selectedVideo.video_id}`}
      allow="fullscreen;accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      style={{
        width: '100%',
        height: '100%',
        minHeight: `${matches ? '220px' : '544px'}`,
        borderRadius: `${matches ? '0' : '8px 0 0 8px'}`,
      }}
    />
  )

  useEffect(() => {
    renderVideoMain()
  }, [selectedVideo])

  return (
    <Box className={classes.main}>
      <Box className={classes.titleContainer}>
        <Typography variant="h4" className={classes.title}>
          Vídeos
        </Typography>
        {matches && (
          <Box
            display="flex"
            alignItems="center"
            flexDirection={matches ? 'column' : 'row'}
            style={{ gap: 24 }}
          >
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <IconButton
                className={classes.iconNavite}
                color="primary"
                onClick={() =>
                  videoList.indexOf(selectedVideo) !== 0
                    ? videoController('prev')
                    : null
                }
              >
                <NavigateBeforeIcon className={classes.icon} />
              </IconButton>
              <IconButton
                className={classes.iconNavite}
                color="primary"
                onClick={() =>
                  filteredVideoList.indexOf(selectedVideo) !==
                  filteredVideoList.length - 1
                    ? videoController('next')
                    : null
                }
              >
                <NavigateNextIcon className={classes.icon} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
      <Box className={classes.videosContainer}>
        <Box className={classes.videoMain}>{renderVideoMain()}</Box>
        <Box className={classes.moreVideos}>
          {filteredVideoList.map((video, index) => (
            <Box
              key={video.video_id}
              onClick={() => setSelectedVideo(video)}
              style={{
                borderRadius: `${
                  !matches && index === 0
                    ? '0 8px 0 0'
                    : index === 3
                    ? '0 0 8px 0'
                    : '0'
                }`,
              }}
              className={
                selectedVideo.video_id === video.video_id
                  ? classes.videPreviewSelected
                  : classes.videPreview
              }
            >
              <img
                alt={`${video.titulo}-thumbnail`}
                className={classes.imageVideoPreview}
                src={`https://img.youtube.com/vi/${video.video_id}/0.jpg`}
              />
              <Typography className={classes.titleVideoPreview}>
                {video.titulo}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Button
        className={classes.button}
        variant="contained"
        onClick={() => history.push('/videos')}
      >
        <Typography className={classes.textButton}>Todos os vídeos</Typography>
      </Button>
    </Box>
  )
}
